import { Edge } from "@xyflow/react"
import { nodeHeight, nodeWidth } from "@features/builder/helpers/nodes"
import { sortNodes } from "@features/builder/graph/helpers"
import {
  AppNode,
  EdgeData,
  ForecastTableNode,
  GroupNode,
  NodeProperties,
  NodeType,
  ScenarioTableNode,
  TableNode,
} from "@features/builder/graph/types"
import {
  EditorGraphDto,
  SocketGroupDto,
  SocketNodeDto,
  SocketTableNode,
} from "@features/builder/types"
import * as Y from "yjs"
import { createNodesEdges } from "@features/builder/helpers/edges"
import { BaseGraphZipNode } from "@store/services/converter/types"
import {
  COLLAPSED_GROUP_Z_INDEX,
  EXPANDED_GROUP_Z_INDEX,
  NODE_Z_INDEX,
} from "@features/builder/graph/Graph"

type EditGraphData = {
  nodes: AppNode[]
  edges: Edge<EdgeData>[]
  originalGraph: EditorGraphDto
}

function getNestedLevelForGroup(
  groupId: string,
  groupMap: { [key: string]: SocketGroupDto },
  nestingLevel = 1,
): number {
  if (nestingLevel > 10) {
    return nestingLevel // prevents deep level
  }
  const parentId = groupMap[groupId]?.parent
  return parentId ? getNestedLevelForGroup(parentId, groupMap, nestingLevel + 1) : nestingLevel
}

function hasCollapsedAncestor(
  parentId: string | undefined,
  groupMap: { [key: string]: GroupNode },
): boolean {
  const visited = new Set<string>() // guards against broken parent cycles
  let currentId = parentId

  while (currentId && !visited.has(currentId)) {
    visited.add(currentId)
    const group = groupMap[currentId]
    if (group?.data.metaGroup.isCollapsed) return true
    currentId = group?.parentId
  }

  return false
}

export const getSocketGroupsMap = (groups: string[]): { [key: string]: SocketGroupDto } => {
  return groups.reduce((obj: { [key: string]: SocketGroupDto }, value) => {
    const item = JSON.parse(value)
    obj[item.id] = item
    return obj
  }, {})
}

export const getReactFlowGroupsMap = (groups: string[]): { [key: string]: GroupNode } => {
  const socketGroupsMap = getSocketGroupsMap(groups)

  return groups.reduce((obj: { [key: string]: GroupNode }, value) => {
    const socketNode = JSON.parse(value)
    obj[socketNode.id] = convertSocketGroupToReactFlowGroup(socketNode, socketGroupsMap)
    return obj
  }, {})
}

export function convertSocketGraphToReactFlow(graphData: EditorGraphDto): EditGraphData {
  const reactFlowGroupsMap = getReactFlowGroupsMap(graphData.groups)

  const result: EditGraphData = {
    nodes: [],
    edges: [],
    originalGraph: graphData,
  }

  // `hidden` is derived from `isCollapsed` of ancestors instead of trusting the stored value.
  // Graph import sets `isCollapsed` without hiding children like `collapseGroup` does.
  for (const val of graphData.groups) {
    const node = JSON.parse(val)
    const groupNode = reactFlowGroupsMap[node.id] as GroupNode

    result.nodes.push({
      ...structuredClone(groupNode),
      hidden: hasCollapsedAncestor(groupNode.parentId, reactFlowGroupsMap),
    })
  }

  for (const val of graphData.nodes) {
    const node = JSON.parse(val)
    const parentGroup = getSocketNodeParent(node, reactFlowGroupsMap)
    const reactFlowNode = convertSocketNodeToReactFlowNode(node, parentGroup)

    result.nodes.push({
      ...structuredClone(reactFlowNode),
      hidden: hasCollapsedAncestor(reactFlowNode.parentId, reactFlowGroupsMap),
    })
  }

  return {
    nodes: sortNodes(result.nodes),
    edges: createNodesEdges(result.nodes),
    originalGraph: graphData,
  }
}

function convertSocketNodeToReactFlowNode(
  node: SocketNodeDto,
  parentGroup?: GroupNode,
): Exclude<AppNode, GroupNode> {
  switch (node.type) {
    case NodeType.Table: {
      const tableNode: TableNode = {
        id: node.id,
        position: node.position,
        parentId: parentGroup ? parentGroup.id : undefined,
        type: node.type,
        connectable: true,
        zIndex: NODE_Z_INDEX,
        hidden: node.hidden,
        style: node.style,
        data: {
          comments: node.data.comments ?? "",
          label: node.data.meta.label,
          nestingLevel: parentGroup?.data.nestingLevel || 1,
          metaNode: structuredClone(node.data.meta),
          originalAttribute: structuredClone(node.data.attribute),
        },
      }
      return tableNode
    }
    case NodeType.ScenarioTable: {
      const scenarioTable: ScenarioTableNode = {
        id: node.id,
        position: node.position,
        parentId: parentGroup ? parentGroup.id : undefined,
        type: node.type,
        connectable: true,
        zIndex: NODE_Z_INDEX,
        hidden: node.hidden,
        style: node.style,
        data: {
          comments: node.data.comments ?? "",
          label: node.data.meta.label,
          nestingLevel: parentGroup?.data.nestingLevel || 1,
          metaNode: structuredClone(node.data.meta),
          originalAttribute: structuredClone(node.data.attribute),
        },
      }
      return scenarioTable
    }
    case NodeType.ForecastTable: {
      const forecastTable: ForecastTableNode = {
        id: node.id,
        position: node.position,
        parentId: parentGroup ? parentGroup.id : undefined,
        type: node.type,
        connectable: true,
        zIndex: NODE_Z_INDEX,
        hidden: node.hidden,
        style: node.style,
        data: {
          comments: node.data.comments ?? "",
          label: node.data.meta.label,
          nestingLevel: parentGroup?.data.nestingLevel || 1,
          metaNode: structuredClone(node.data.meta),
          originalAttribute: structuredClone(node.data.attribute),
        },
      }
      return forecastTable
    }
  }
  return {
    id: node.id,
    position: node.position,
    parentId: parentGroup ? parentGroup.id : undefined,
    type: node.type,
    connectable: true,
    zIndex: NODE_Z_INDEX,
    hidden: node.hidden,
    style: node.style,
    data: {
      comments: node.data.comments ?? "",
      label: node.data.meta.label,
      nestingLevel: parentGroup?.data.nestingLevel || 1,
      metaNode: structuredClone(node.data.meta),
      originalNode: structuredClone(node.data.node) as BaseGraphZipNode<NodeProperties>,
    },
  }
}

function convertSocketGroupToReactFlowGroup(
  group: SocketGroupDto,
  groupMap: { [key: string]: SocketGroupDto },
): GroupNode {
  const metaGroup = structuredClone(group.data.meta)
  const { isCollapsed } = metaGroup

  // Graph import sets `isCollapsed` but keeps the expanded size,
  // so shrink it and remember the size for expanding, like `collapseGroup` does.
  if (isCollapsed) metaGroup.expanded ??= structuredClone(group.style)

  return {
    id: group.id,
    position: group.position,
    parentId: group.parent,
    type: NodeType.Group,
    className: "group-node",
    style: isCollapsed ? { width: nodeWidth, height: nodeHeight } : group.style,
    zIndex: isCollapsed ? COLLAPSED_GROUP_Z_INDEX : EXPANDED_GROUP_Z_INDEX,
    hidden: group.hidden,
    data: {
      comments: group.data.comments ?? "",
      nestingLevel: getNestedLevelForGroup(group.id, groupMap), // TODO: calculate nesting level
      label: group.data.meta.label,
      metaGroup,
    },
  }
}

export const convertReactFlowNodeToSocketNode = (
  node: Exclude<AppNode, GroupNode>,
): SocketNodeDto => {
  switch (node.type) {
    case NodeType.Table: {
      const tableNode: SocketTableNode = {
        id: node.id,
        position: { x: Math.round(node.position.x), y: Math.round(node.position.y) },
        style: { width: nodeWidth, height: nodeHeight },
        type: NodeType.Table,
        hidden: node.hidden,
        data: {
          comments: node.data.comments ?? "",
          meta: node.data.metaNode,
          attribute: node.data.originalAttribute,
        },
      }

      return tableNode
    }
    case NodeType.ScenarioTable: {
      const tableNode: SocketTableNode = {
        id: node.id,
        position: { x: Math.round(node.position.x), y: Math.round(node.position.y) },
        style: { width: nodeWidth, height: nodeHeight },
        type: NodeType.ScenarioTable,
        hidden: node.hidden,
        data: {
          comments: node.data.comments ?? "",
          meta: node.data.metaNode,
          attribute: node.data.originalAttribute,
        },
      }

      return tableNode
    }
    case NodeType.ForecastTable: {
      const tableNode: SocketTableNode = {
        id: node.id,
        position: { x: Math.round(node.position.x), y: Math.round(node.position.y) },
        style: { width: nodeWidth, height: nodeHeight },
        type: NodeType.ForecastTable,
        hidden: node.hidden,
        data: {
          comments: node.data.comments ?? "",
          meta: node.data.metaNode,
          attribute: node.data.originalAttribute,
        },
      }

      return tableNode
    }
  }

  return {
    id: node.id,
    position: { x: Math.round(node.position.x), y: Math.round(node.position.y) },
    style: { width: nodeWidth, height: nodeHeight },
    type: node.type!,
    hidden: node.hidden,
    data: {
      comments: node.data.comments ?? "",
      meta: node.data.metaNode,
      node: (node as Exclude<typeof node, TableNode>).data.originalNode, // exclude table - handled above
    },
  }
}

export const convertReactFlowGroupToSocketGroup = (node: GroupNode): SocketGroupDto => {
  return {
    id: node.id,
    position: { x: Math.round(node.position.x), y: Math.round(node.position.y) },
    style: {
      width: Number(node?.style?.width) || nodeWidth,
      height: Number(node?.style?.height) || nodeHeight,
    },
    type: node.type as NodeType.Group,
    parent: node.parentId,
    hidden: node.hidden ?? false,
    data: {
      comments: node.data.comments ?? "",
      meta: node.data.metaGroup!,
    },
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
export const toCrdt = (data: any) => {
  if (typeof data === "string") {
    return new Y.Text(data)
  } else if (Array.isArray(data)) {
    const yArray = new Y.Array()
    data.forEach((item) => yArray.push([toCrdt(item)]))

    return yArray
  } else if (typeof data === "object" && data !== null) {
    const yMap = new Y.Map()
    Object.entries(data).forEach(([key, value]) => yMap.set(key, toCrdt(value)))

    return yMap
  } else {
    return data
  }
}

const getSocketNodeParent = (
  node: SocketNodeDto,
  groupMap: { [key: string]: GroupNode },
): GroupNode => {
  const groupIds = node.data.meta.groups ?? []
  const groups = groupIds.map((groupId) => groupMap[groupId]).filter((group) => group !== undefined)

  const groupsSortedByNestingLevel = groups.toSorted(
    (groupA, groupB) => groupB.data.nestingLevel - groupA.data.nestingLevel,
  )

  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  return groupsSortedByNestingLevel[0]
}
